import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";

import React from "react";
import { useTranslation } from "react-i18next";

import { DoneSmallIcon } from "./DoneSmallIcon";

export type StepProps = {
  color?: string;
  buttonColor?: string;
  buttonVariant?: string;
  textColor?: string;
  step?: number;
  titleLoc?: string;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  buttonLabelLoc?: string;
  replayLabelLoc?: string;
  secondaryButtonLabelLoc?: string;
  secondaryButtonTooltipLoc?: string;
  onSecondaryClick?: () => void;
  onClick?: () => void;
  done?: boolean;
  showDone?: boolean;
  loading?: boolean;
  infoIcon?: boolean;
  disabled?: boolean;
};

export const Step: React.FC<StepProps> = ({
  color = "secondary",
  buttonColor = "primary",
  buttonVariant = "contained",
  textColor,
  step,
  titleLoc,
  description,
  icon,
  buttonLabelLoc,
  replayLabelLoc,
  secondaryButtonLabelLoc,
  secondaryButtonTooltipLoc,
  onSecondaryClick,
  done = false,
  onClick = () => null,
  showDone = true,
  loading = false,
  infoIcon,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const isButtonDisabled = disabled || loading;
  const showSecondaryButton = !done && secondaryButtonLabelLoc && onSecondaryClick;

  return (
    <Box bgcolor={`${color}.main`} px={3} py={2} borderRadius={1}>
      <Stack direction="row" alignItems={!infoIcon ? "center" : "stretch"} spacing={2}>
        <Box pt="4px">
          <Stack justifyContent="top" alignItems="center" minHeight="36px" minWidth="36px">
            {icon}
          </Stack>
        </Box>

        <Box flexGrow={1}>
          <Typography variant="caption" sx={{ color: color === "secondary" ? "#777373" : "#FFF" }} color={textColor}>
            {t(`common.step${step}`)}
          </Typography>

          <Typography variant="body1" color={textColor}>
            {titleLoc ? t(titleLoc) : ""}
          </Typography>
          {description && (
            <Typography mt={1} variant="body2" color={textColor}>
              {typeof description === "string" ? t(description) : description}
            </Typography>
          )}
        </Box>
        {done && replayLabelLoc && (
          <Button size="small" loading={loading} onClick={onClick} disabled={isButtonDisabled} sx={{ flexShrink: 0 }}>
            {t(replayLabelLoc)}
          </Button>
        )}
        {done && showDone && <DoneSmallIcon />}
        {!done && (buttonLabelLoc || showSecondaryButton) && (
          <Stack justifyContent="center" alignItems="center" direction="row" spacing={1}>
            {buttonLabelLoc && (
              <Tooltip title={!done && disabled ? t("signedFlow.completePreviousStepFirst") : ""} placement="top">
                <span>
                  <Button
                    loading={loading}
                    color={buttonColor as any}
                    variant={buttonVariant as any}
                    onClick={onClick}
                    disabled={isButtonDisabled}
                    disableElevation
                    size="small"
                    style={{ borderRadius: 100, padding: "8px 16px" }}
                  >
                    {t(buttonLabelLoc)}
                  </Button>
                </span>
              </Tooltip>
            )}
            {showSecondaryButton && (
              <Tooltip title={secondaryButtonTooltipLoc ? t(secondaryButtonTooltipLoc) : ""} placement="top">
                <span>
                  <Button
                    variant="text"
                    color={buttonColor as any}
                    onClick={onSecondaryClick}
                    disabled={isButtonDisabled}
                    disableElevation
                    size="small"
                    sx={{ fontSize: "0.7rem", textTransform: "none", minWidth: 0, py: 0.25, px: 0.75 }}
                  >
                    {t(secondaryButtonLabelLoc)}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
